import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { pb, type User } from '../lib/pb';

// Query keys for auth-related queries
export const authKeys = {
  user: ['auth', 'user'] as const,
  session: ['auth', 'session'] as const,
};

// Main hook for accessing auth state and actions
export const useAuth = () => {
  const authStore = useAuthStore();
  
  return {
    user: authStore.user,
    isLoading: authStore.isLoading,
    error: authStore.error,
    isAuthenticated: !!authStore.user,
    isInitialized: authStore.isInitialized,
    
    // Actions
    login: authStore.login,
    register: authStore.register,
    logout: authStore.logout,
    clearError: authStore.clearError,
    refreshAuth: authStore.refreshAuth,
  };
};

// Hook for login mutation
export const useLogin = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { login, clearError } = useAuthStore();
  
  return useMutation({
    mutationFn: async ({ email, password }: { email: string; password: string }) => {
      clearError(); // Clear any previous errors
      await login(email, password);
    },
    onSuccess: () => {
      // Redirect to dashboard on successful login
      navigate('/dashboard');
      
      // Invalidate all queries to refetch with auth context
      queryClient.invalidateQueries();
    },
    onError: (error) => {
      console.error('Login failed:', error);
      // Error is already set in the store by the login action
    },
  });
};

// Hook for registration mutation
export const useRegister = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { register, clearError } = useAuthStore();
  
  return useMutation({
    mutationFn: async ({ 
      email, 
      password, 
      name 
    }: { 
      email: string; 
      password: string; 
      name?: string; 
    }) => {
      clearError(); // Clear any previous errors
      await register(email, password, name);
    },
    onSuccess: () => {
      // Redirect to dashboard on successful registration
      navigate('/dashboard');
      
      // Invalidate all queries to refetch with auth context
      queryClient.invalidateQueries();
    },
    onError: (error) => {
      console.error('Registration failed:', error);
      // Error is already set in the store by the register action
    },
  });
};

// Hook for logout mutation
export const useLogout = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { logout } = useAuthStore();
  
  return useMutation({
    mutationFn: async () => {
      logout();
    },
    onSuccess: () => {
      // Clear all cached data
      queryClient.clear();
      
      // Redirect to login
      navigate('/login');
    },
  });
};

// Hook to check current auth status (useful for components)
export const useAuthStatus = () => {
  const { user, isLoading, isInitialized, isAuthenticated } = useAuth();
  
  return useQuery({
    queryKey: authKeys.session,
    queryFn: () => ({
      user,
      isAuthenticated,
      isInitialized,
    }),
    staleTime: Infinity, // This data comes from Zustand store, no need to refetch
    enabled: isInitialized, // Only run after auth is initialized
  });
};

// Hook for refreshing auth token
export const useRefreshAuth = () => {
  const { refreshAuth } = useAuthStore();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: refreshAuth,
    onSuccess: () => {
      // Invalidate auth-related queries
      queryClient.invalidateQueries({ queryKey: authKeys.user });
      queryClient.invalidateQueries({ queryKey: authKeys.session });
    },
    onError: () => {
      // If refresh fails, user will be logged out by the store
      queryClient.clear();
    },
  });
};